/**
 * §6.1 — does this item apply to this project?
 *
 * "Contextual" means `contextCondition !== null` (§4.2), never `marker === 'C'`.
 * An item that does not apply leaves BOTH sides of the score; it is not a failure.
 */

import { evaluateContextExpr } from './context-expression.js';
import type { ContextProfile, ItemDefinition } from './types.js';

/** Contextual items are the only ones that may be answered NOT_APPLICABLE (§6.7 vector 5). */
export function isContextual(item: Pick<ItemDefinition, 'contextCondition'>): boolean {
  return item.contextCondition !== null;
}

export function isApplicable(
  item: Pick<ItemDefinition, 'contextCondition'>,
  profile: ContextProfile,
): boolean {
  if (item.contextCondition === null) {
    return true;
  }
  return evaluateContextExpr(item.contextCondition, profile);
}

export function applicableItems<T extends Pick<ItemDefinition, 'contextCondition'>>(
  items: readonly T[],
  profile: ContextProfile,
): T[] {
  return items.filter((item) => isApplicable(item, profile));
}

export interface ApplicabilityDiff {
  /** Refs that become applicable under the new profile. */
  readonly entering: string[];
  /** Refs that stop being applicable. */
  readonly leaving: string[];
  /** Counts the wizard's scope panel renders. */
  readonly totals: {
    readonly before: { readonly applicable: number; readonly mandatory: number };
    readonly after: { readonly applicable: number; readonly mandatory: number };
  };
}

/**
 * The dry-run behind `PUT /projects/:id/context?dryRun=true`.
 *
 * `WizardContext.dc.html`'s confirmation diff is not decoration — it is the write
 * path for a record, so the numbers it shows come from here and not from the form.
 */
export function applicabilityDiff(
  items: readonly ItemDefinition[],
  before: ContextProfile,
  after: ContextProfile,
): ApplicabilityDiff {
  const entering: string[] = [];
  const leaving: string[] = [];
  let applicableBefore = 0;
  let applicableAfter = 0;
  let mandatoryBefore = 0;
  let mandatoryAfter = 0;

  for (const item of items) {
    const wasApplicable = isApplicable(item, before);
    const isNowApplicable = isApplicable(item, after);
    const isMandatory = item.obligation === 'MANDATORY';

    if (wasApplicable) {
      applicableBefore += 1;
      if (isMandatory) mandatoryBefore += 1;
    }
    if (isNowApplicable) {
      applicableAfter += 1;
      if (isMandatory) mandatoryAfter += 1;
    }

    if (!wasApplicable && isNowApplicable) entering.push(item.ref);
    if (wasApplicable && !isNowApplicable) leaving.push(item.ref);
  }

  return {
    entering,
    leaving,
    totals: {
      before: { applicable: applicableBefore, mandatory: mandatoryBefore },
      after: { applicable: applicableAfter, mandatory: mandatoryAfter },
    },
  };
}
